/**
 * Deep ActionFlow validation rules — checks runtime compatibility.
 * Supplements the structural checks in stage-actionflow.ts.
 *
 * Rules:
 *  1. actionflow-setState-operation-valid — operation must be set/append/upsert/removeWhere/updateWhere
 *  2. actionflow-setState-params-complete — operation-specific required params
 *  3. actionflow-restApi-params-complete — url + method required, POST needs body
 *  4. actionflow-compute-target-valid — target should exist in state
 *  5. actionflow-navigate-route-valid — route should start with /
 *  6. actionflow-expression-balanced — #{} balance check
 *  7. actionflow-tryCatch-structure — required try and optional catch/finally must be arrays
 *  8. actionflow-init-no-event-ref — init trigger should not reference #{event.*}
 *  9. actionflow-setState-target-format — target should start with # (warning)
 * 10. actionflow-step-id-unique — no duplicate step ids in same flow
 * 11. actionflow-setState-array-requires-operation — array target needs append/prepend/upsert (not set/replace/missing)
 * 12. actionflow-setState-upsert-missing-match — upsert needs matchExpr + onNew/onMatch (R5b)
 * 13. actionflow-expression-not-bound — bare state/event references need #{…} wrapping (R6)
 */

import type { ValidationIssue } from "../types"
import { loadValidOperations } from "../io/stages/ontology-loader"
import { expandDottedStateKeys } from "./state-key-prefixes"
import { resolveRuntimeAction } from "../vocab/runtime-action-resolution"
import accessorGlobals from "../../ontology/expression-accessor-globals.v1.json" with { type: "json" }
import { forEachNestedStepArray } from "./nested-step-keys"

// setState operations — loaded from action-types ontology
const VALID_OPERATIONS = loadValidOperations()

// Exported (genquality-census-class fix, 2026-08-07): this is the single
// authoritative operation→required-params map for setState. It used to be
// consulted ONLY by the flow-embedded step check below (`walkStepsDeep`,
// rule `actionflow-setState-params-complete`, severity warning) — a
// same-shape setState call placed directly on an event binding instead of
// inside a flow was checked by a completely different, operation-unaware
// path in stage-actionflow.ts (`validateDirectEventBindings`), which always
// demanded `value` regardless of `operation`. That path never reads
// `operation` at all, so `operation: "updateWhere"` (which needs `predicate`
// + `update`, never `value` — see action-flow-engine.ts#actionSetState)
// false-positived as "missing required param: value" purely because of
// binding *style* (direct action vs named flow), not content. Exporting this
// map lets both call sites share one fact instead of two silently-diverging
// copies of the same semantics.
export const OPERATION_REQUIRED_PARAMS: Record<string, string[]> = {
  set: ["value"],
  removeWhere: ["predicate"],
  updateWhere: ["predicate"],
  append: ["value"],
}

export function runActionFlowDeepRules(
  flows: Record<string, unknown>,
  state: Record<string, unknown>,
  globalState?: Record<string, unknown>,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  // Expand flat-dotted state keys into namespace prefixes so lookups like
  // stateKeys.has('abcAnalysis') resolve when state.json declares
  // 'abcAnalysis.rows' / 'abcAnalysis.pageIndex' / etc.
  const stateKeys = expandDottedStateKeys(Object.keys(state))
  const globalPrefixes = ["appData", "_results", "isLoading", "error", "_loading", "_error"]
  const arrayTargets = inferArrayTargets(state, globalState ?? {})

  for (const [flowName, flowDef] of Object.entries(flows)) {
    if (typeof flowDef !== "object" || flowDef === null) continue
    const fd = flowDef as Record<string, unknown>
    const trigger = fd.trigger as string | undefined
    const steps = fd.steps
    if (!Array.isArray(steps)) continue
    const seenIds = new Set<string>()
    walkStepsDeep(issues, flowName, steps as Record<string, unknown>[], stateKeys, globalPrefixes, trigger, seenIds, arrayTargets)
    checkReactiveFlowSafety(issues, flowName, fd, steps as Record<string, unknown>[])
  }

  return issues
}

/** Normalize a trigger that may be a bare string or a { type } object (stage-webmcp allows both). */
function triggerName(raw: unknown): string | undefined {
  if (typeof raw === "string") return raw
  if (raw && typeof raw === "object" && typeof (raw as Record<string, unknown>).type === "string") {
    return (raw as Record<string, unknown>).type as string
  }
  return undefined
}

/** Walk ALL nested step arrays (then/else/steps/try/catch/finally/default + switch cases) collecting
 *  emitted event names and whether any guard (conditional/switch/condition) is present. */
function collectReactiveFacts(
  steps: Record<string, unknown>[],
  facts: { emits: Set<string>; hasGuard: boolean },
): void {
  for (const step of steps) {
    if (!step || typeof step !== "object") continue
    const type = step.type as string | undefined
    const action = step.action as string | undefined
    const canonicalAction = action ? resolveRuntimeAction(action)?.canonicalAction ?? action : undefined
    if (type === "conditional" || type === "switch" || typeof step.condition === "string") facts.hasGuard = true
    if (canonicalAction === "emitEvent") {
      const p = step.params as Record<string, unknown> | undefined
      const name = (p?.event ?? p?.eventName ?? p?.name) as unknown
      if (typeof name === "string" && name) facts.emits.add(name)
    }
    forEachNestedStepArray(step, (nested) => collectReactiveFacts(nested, facts))
  }
}

/**
 * Safety + hygiene for REACTIVE flows (tick trigger / `on:` emission listeners). The runtime
 * delegates loop-bounding to the flow author (action-flow-engine actionEmitEvent / runTickFlows),
 * so the validator is the only place to catch a runaway. A guard (conditional/switch reading
 * state) is treated as the termination affordance — a guarded reactive flow is assumed bounded.
 *   - on:X that re-emits X with NO guard  → hard-block (guaranteed infinite cascade)
 *   - on:X / tick that emits with NO guard → advisory (likely-unbounded loop / event flood)
 *   - unknown trigger value                → advisory (typo / stale vocabulary)
 * A bare continuous tick (no emit, no guard) is fine (render/animation loop) and not flagged.
 */
function checkReactiveFlowSafety(
  issues: ValidationIssue[],
  flowName: string,
  fd: Record<string, unknown>,
  steps: Record<string, unknown>[],
): void {
  const trig = triggerName(fd.trigger)
  const onName = typeof fd.on === "string" ? (fd.on as string) : undefined
  if (trig !== "tick" && !onName) return // not a reactive flow

  const facts = { emits: new Set<string>(), hasGuard: false }
  collectReactiveFacts(steps, facts)
  if (facts.hasGuard) return // a guard is the termination affordance → assume bounded

  if (onName && facts.emits.has(onName)) {
    issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-self-reemit-unguarded", message: `Flow '${flowName}' subscribes to '${onName}' (on:) and re-emits '${onName}' with no guard — an unbounded emission cascade. Gate the re-emit behind a conditional (e.g. condition "#{local.phase != 'done'}") so the loop terminates.` })
  } else if ((onName || trig === "tick") && facts.emits.size > 0) {
    issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-unbounded-reactive-loop", message: `Flow '${flowName}' (${onName ? `on:'${onName}'` : "tick trigger"}) emits an event with no termination guard — risk of an unbounded loop / event flood. Add a conditional guard (e.g. a #{local.phase}/counter check) before the emit.` })
  }
}

/**
 * Does this setState value already evaluate to the complete new array?
 *
 * Three shapes count, and none is a guess about intent:
 *   - a JSON array literal (`[]`, `[{...}]`)
 *   - an expression whose body is an array literal — `#{[...state.toasts, x]}`
 *   - an expression containing a call to an accessor the ontology declares
 *     `cogo:accessorReturnKind "array"` (map / filter / sort / slice / split /
 *     unique and their $-prefixed forms), projected into
 *     ontology/expression-accessor-globals.v1.json
 *
 * Anything else — a scalar, an object literal, a bare path — is the case the
 * rule exists for: one item assigned to an array target, where the author
 * probably meant append.
 */
type ArrayValueShape = "whole-array" | "opaque-path" | "not-an-array"

function classifySetStateValue(value: unknown): ArrayValueShape {
  if (Array.isArray(value)) return "whole-array"
  if (typeof value !== "string") return "not-an-array"
  const expr = /^#\{([\s\S]*)\}$/.exec(value.trim())?.[1]?.trim()
  if (!expr) return "not-an-array"
  if (expr.startsWith("[")) return "whole-array"
  const arrayReturning: readonly string[] = accessorGlobals.arrayReturning
  if (arrayReturning.some((name) => expr.includes(name.startsWith("$") ? `${name}(` : `.${name}(`))) {
    return "whole-array"
  }
  // A bare dotted path — `#{_results.mediaLibrary.data}`, `#{event.item}`.
  // Whether it holds an array or one item is not written down anywhere the
  // validator can read: the `_results` prefix declares "Named result of a
  // prior ActionFlow step", not its shape. Reporting an error on a question
  // it cannot answer is what this rule was doing with flow names.
  if (/^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/.test(expr)) return "opaque-path"
  return "not-an-array"
}

function inferArrayTargets(state: Record<string, unknown>, globalState: Record<string, unknown>): Set<string> {
  const out = new Set<string>()
  const scan = (container: Record<string, unknown>, prefix: string) => {
    for (const [k, v] of Object.entries(container ?? {})) {
      const path = prefix ? `${prefix}.${k}` : k
      if (Array.isArray(v)) {
        out.add(path)
        out.add(k)
        continue
      }
      if (v && typeof v === "object") {
        const entry = v as Record<string, unknown>
        if (entry.type === "array" || Array.isArray(entry.default)) {
          out.add(path)
          out.add(k)
        } else if (entry.default && typeof entry.default === "object" && !Array.isArray(entry.default)) {
          scan(entry.default as Record<string, unknown>, path)
        } else {
          scan(entry, path)
        }
      }
    }
  }
  scan(state, "")
  for (const [root, entry] of Object.entries(globalState ?? {})) {
    if (entry && typeof entry === "object" && "default" in (entry as Record<string, unknown>)) {
      scan({ [root]: (entry as Record<string, unknown>).default } as Record<string, unknown>, "")
    }
  }
  return out
}

function walkStepsDeep(
  issues: ValidationIssue[],
  flowName: string,
  steps: Record<string, unknown>[],
  stateKeys: Set<string>,
  globalPrefixes: string[],
  trigger: string | undefined,
  seenIds: Set<string>,
  arrayTargets: Set<string>,
): void {
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    if (!step || typeof step !== "object") continue

    const action = step.action as string | undefined
    const canonicalAction = action ? resolveRuntimeAction(action)?.canonicalAction ?? action : undefined
    const params = step.params as Record<string, unknown> | undefined
    const stepType = step.type as string | undefined

    // Rule 10: duplicate step id
    const stepId = step.id as string | undefined
    if (stepId) {
      if (seenIds.has(stepId)) {
        issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-step-id-unique", message: `Flow '${flowName}' has duplicate step id '${stepId}'` })
      }
      seenIds.add(stepId)
    }

    if (canonicalAction === "setState" && params) {
      // Rule 9 (v5.8): target format. Bare keys are correct — runtime strips
      // any leading '#' (action-flow-engine.ts:490). Conflict resolution with
      // stage-convention.ts:354 which already warns when '#' IS present.
      // No warning for bare keys.

      // Rule 1: valid operation
      const op = params.operation as string | undefined
      if (op && !VALID_OPERATIONS.has(op)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-setState-operation-valid", message: `Flow '${flowName}' step[${i}] setState operation '${op}' is invalid. Valid: ${[...VALID_OPERATIONS].join(", ")}` })
      }

      // Rule 2: operation params complete
      if (op && OPERATION_REQUIRED_PARAMS[op]) {
        for (const req of OPERATION_REQUIRED_PARAMS[op]) {
          if (!(req in params)) {
            issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-setState-params-complete", message: `Flow '${flowName}' step[${i}] setState operation '${op}' missing required param '${req}'` })
          }
        }
      }

      // Rule R5b: upsert MUST have matchExpr (or legacy `match`) AND at least
      // one of onNew/onMatch. Runtime silently no-ops otherwise — documented in
      // spec-2026-04-15 Open Question #2 closure.
      if (op === "upsert") {
        const hasMatch =
          (typeof params.matchExpr === "string" && (params.matchExpr as string).trim().length > 0) ||
          (typeof params.match === "string" && (params.match as string).trim().length > 0)
        const hasOnNew = params.onNew != null
        const hasOnMatch = params.onMatch != null
        if (!hasMatch || (!hasOnNew && !hasOnMatch)) {
          const missing: string[] = []
          if (!hasMatch) missing.push("'matchExpr'")
          if (!hasOnNew && !hasOnMatch) missing.push("at least one of 'onNew' / 'onMatch'")
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-setState-upsert-missing-match",
            message: `Flow '${flowName}' step[${i}] setState upsert missing ${missing.join(" and ")}. Upsert without these silently no-ops at runtime.`,
          })
        }
      }

      // Rule R5a: setState target resolves to an array declaration but operation
      // is a mutating one that silently overwrites. Softening (W4, 2026-04-18):
      //   • operation === "replace"  → PASS (explicit full-replace marker; runtime
      //     default branch already treats it as setState(target, value)).
      //   • operation omitted AND flow name matches load/fetch/refresh/… pattern
      //     → INFO (full-replace load is the dominant idiom; value is the entire
      //     new array from API response). Generator should still emit "replace"
      //     for clarity once prompts are updated.
      //   • operation === "set" → ERROR (array vs scalar semantic clash).
      //   • operation omitted AND flow name does NOT look like a load/query
      //     → ERROR (likely a forgotten append/upsert on a mutation flow).
      const rawTarget = typeof params.target === "string" ? params.target : ""
      const normTarget = rawTarget.replace(/^#\{?|\}$/g, "")
      const isArrayTarget =
        arrayTargets.has(normTarget) ||
        (normTarget.includes(".") && arrayTargets.has(normTarget.split(".").pop() as string))
      if (isArrayTarget && (!op || op === "set" || op === "replace")) {
        if (op === "replace") {
          // Explicit full-replace — allowed. No issue.
        } else if (classifySetStateValue(params.value) === "whole-array") {
          // The value IS the whole new array — `[]`, `#{[...state.toasts, x]}`,
          // `#{local.items.filter(...)}` — which is exactly what the runtime's
          // default branch writes. Nothing is being silently dropped.
          //
          // This replaced a regex on the FLOW NAME (2026-08-25). That branch
          // downgraded to info only when the flow was called load/fetch/
          // refresh/…, so blocks-v2's `addToast`, `removeFile`, `markAllRead`,
          // `clearAllFiles` and `cancelUpload` were errors — all 10 of the
          // rule's errors — while every one of them computed a complete array
          // and assigned it. `set`, `replace` and an omitted operation are
          // indistinguishable to actionSetState for an ARRAY value (its merge
          // branch is guarded by `!Array.isArray`), so the flow's name was
          // deciding a question the value already answers.
        } else if (op === "set") {
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-setState-array-requires-operation",
            message: `Flow '${flowName}' step[${i}] setState target '${normTarget}' is an array — use operation: "append" | "prepend" | "upsert" | "updateAt" | "removeAt" | "replace" instead of '${op}'`,
          })
        } else if (classifySetStateValue(params.value) === "opaque-path") {
          issues.push({
            stage: "actionflow",
            level: "info",
            file: "flow.json",
            rule: "actionflow-setState-array-requires-operation",
            message: `Flow '${flowName}' step[${i}] setState target '${normTarget}' is an array assigned a bare path — if '${String(params.value)}' holds the whole new array, say operation: "replace"; if it holds one item, "append"`,
          })
        } else {
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-setState-array-requires-operation",
            message: `Flow '${flowName}' step[${i}] setState target '${normTarget}' is an array but the value is not one — use operation: "append" | "prepend" | "upsert" | "updateAt" | "removeAt", or operation: "replace" if a full overwrite is intended`,
          })
        }
      }
    }

    // Rule 3: restApi params
    if (canonicalAction === "restApi" && params) {
      if (!params.url) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-restApi-params-complete", message: `Flow '${flowName}' step[${i}] ${action} missing 'url'` })
      }
      if (!params.method) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-restApi-params-complete", message: `Flow '${flowName}' step[${i}] ${action} missing 'method'` })
      }
      const method = (params.method as string ?? "").toUpperCase()
      if (["POST", "PUT", "PATCH"].includes(method) && !params.body) {
        issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-restApi-params-complete", message: `Flow '${flowName}' step[${i}] ${action} ${method} should have 'body'` })
      }
    }

    // Rule 4: compute target
    if (canonicalAction === "compute" && params) {
      const target = (params.target as string ?? "").replace(/^#/, "")
      const topLevel = target.split(".")[0]
      const isGlobal = globalPrefixes.some(p => target.startsWith(p))
      if (topLevel && !isGlobal && !stateKeys.has(topLevel) && stateKeys.size > 0) {
        issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-compute-target-valid", message: `Flow '${flowName}' step[${i}] compute target '#${target}' — '${topLevel}' not found in state` })
      }
    }

    // Rule 5: navigate route
    if (canonicalAction === "navigate" && params) {
      const route = params.route as string | undefined
      if (route && !route.startsWith("/") && !route.startsWith("#{")) {
        issues.push({ stage: "actionflow", level: "info", file: "flow.json", rule: "actionflow-navigate-route-valid", message: `Flow '${flowName}' step[${i}] navigate route '${route}' should start with '/'` })
      }
    }

    // Rule 6: expression balance
    if (params && typeof params === "object") {
      for (const [key, val] of Object.entries(params)) {
        if (typeof val !== "string") continue
        const opens = (val.match(/#\{/g) ?? []).length
        const closes = (val.match(/\}/g) ?? []).length
        if (opens > 0 && opens > closes) {
          issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-expression-balanced", message: `Flow '${flowName}' step[${i}] param '${key}' has unbalanced expression: ${opens} opens, ${closes} closes` })
        }
      }
    }

    // Rule R6: expression binding. Bare references like "event.item" must be
    // wrapped as "#{event.item}" — the runtime treats unwrapped strings as
    // literals, so downstream reads the raw path string instead of the value.
    // Skip `target`: setState uses the `#path` leading-hash form without braces.
    if (params && typeof params === "object") {
      const BIND_RE = /^(event|_args|_results|appData|_system|localState)\./
      for (const key of ["value", "expression", "to", "route"] as const) {
        const raw = (params as Record<string, unknown>)[key]
        if (typeof raw !== "string") continue
        // Already interpolated (#{…}) or setState-target form (#path) → skip.
        if (raw.includes("#{") || raw.startsWith("#")) continue
        if (BIND_RE.test(raw)) {
          issues.push({
            stage: "actionflow",
            level: "error",
            file: "flow.json",
            rule: "actionflow-expression-not-bound",
            message: `Flow '${flowName}' step[${i}] param '${key}' is a bare state/event reference '${raw}' — wrap as '#{${raw}}' for the runtime to evaluate.`,
          })
        }
      }
    }

    // Rule 7: tryCatch structure
    if (stepType === "tryCatch") {
      if (!Array.isArray(step.try)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-tryCatch-structure", message: `Flow '${flowName}' step[${i}] tryCatch 'try' must be an array` })
      }
      if (step.catch !== undefined && !Array.isArray(step.catch)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-tryCatch-structure", message: `Flow '${flowName}' step[${i}] tryCatch 'catch' must be an array` })
      }
      if (step.finally !== undefined && !Array.isArray(step.finally)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-tryCatch-structure", message: `Flow '${flowName}' step[${i}] tryCatch 'finally' must be an array` })
      }
      if (!Array.isArray(step.catch) && !Array.isArray(step.finally)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-tryCatch-structure", message: `Flow '${flowName}' step[${i}] tryCatch requires at least one 'catch' or 'finally' array` })
      }
    }

    if (stepType === "stage") {
      if (typeof step.name !== "string" || step.name.trim().length === 0) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-stage-structure", message: `Flow '${flowName}' step[${i}] stage requires a non-empty 'name'` })
      }
      if (!Array.isArray(step.steps)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-stage-structure", message: `Flow '${flowName}' step[${i}] stage 'steps' must be an array` })
      }
      for (const hook of ["onEnter", "onSuccess", "onError", "onFinally", "compensate"] as const) {
        if (step[hook] !== undefined && !Array.isArray(step[hook])) {
          issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-stage-structure", message: `Flow '${flowName}' step[${i}] stage '${hook}' must be an array when present` })
        }
      }
      if (step.failurePolicy !== undefined && step.failurePolicy !== "stop" && step.failurePolicy !== "continue") {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-stage-structure", message: `Flow '${flowName}' step[${i}] stage failurePolicy must be 'stop' or 'continue'` })
      }
      if (step.failurePolicy === "continue" && (!Array.isArray(step.onError) || step.onError.length === 0)) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-stage-structure", message: `Flow '${flowName}' step[${i}] continuing stage requires a non-empty 'onError' array` })
      }
    }

    if (stepType === "saga") {
      if (!Array.isArray(step.stages) || step.stages.length === 0) {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-saga-structure", message: `Flow '${flowName}' step[${i}] saga requires a non-empty 'stages' array` })
      }
      if (step.compensationFailurePolicy !== undefined && step.compensationFailurePolicy !== "continue" && step.compensationFailurePolicy !== "stop") {
        issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-saga-structure", message: `Flow '${flowName}' step[${i}] saga compensationFailurePolicy must be 'continue' or 'stop'` })
      }
      if (Array.isArray(step.stages)) {
        for (const [stageIndex, stageStep] of step.stages.entries()) {
          if (!stageStep || typeof stageStep !== "object" || (stageStep as Record<string, unknown>).type !== "stage") {
            issues.push({ stage: "actionflow", level: "error", file: "flow.json", rule: "actionflow-saga-structure", message: `Flow '${flowName}' step[${i}] saga stages[${stageIndex}] must be a stage step` })
          }
        }
      }
    }

    // Rule 8: init flow + event ref
    if (trigger === "init" && params && typeof params === "object") {
      for (const [key, val] of Object.entries(params)) {
        if (typeof val === "string" && val.includes("#{event.")) {
          issues.push({ stage: "actionflow", level: "warning", file: "flow.json", rule: "actionflow-init-no-event-ref", message: `Flow '${flowName}' (init trigger) step[${i}] param '${key}' references '#{event.*}' which is unavailable in init flows` })
        }
      }
    }

    // Recurse nested steps — vocabulary owned by stages/nested-step-keys.ts
    forEachNestedStepArray(step, (nested) => {
      walkStepsDeep(issues, flowName, nested, stateKeys, globalPrefixes, trigger, seenIds, arrayTargets)
    })
  }
}
